/**
 * Parse an exception note body into renderable React nodes, turning embedded
 * URLs into safe links.
 *
 * Rules:
 * - Only URLs whose host is on the allow-list (`getLinkDomains()`, sourced from
 *   AWS Parameter Store with an in-code fallback) become links. Anything else
 *   is replaced with a muted "[link removed]" marker - never a live link.
 * - The rendered `href` is always upgraded to https, even if the author typed
 *   http. The displayed text is left as the author typed it.
 * - Freshdesk ticket URLs render compactly as a ticket-icon chip "#<id>"
 *   instead of the raw URL.
 * - Every link opens in a new tab (`target="_blank" rel="noopener noreferrer"`).
 * - Non-URL text (including newlines) is preserved verbatim so the parent
 *   `<Typography whiteSpace="pre-wrap">` keeps the author's line breaks.
 *
 * The URL detection + classification here is the single source of truth: the
 * note-input validation (block submitting a note with a disallowed link) reuses
 * `findDisallowedLinks()` so display and write rules can never drift.
 */
import React from 'react'
import { Box, Link } from '@mui/material'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import { poolwerxColors } from '../theme/colors'
import { getLinkDomains } from './emailValidation'
import { FRESHDESK_PORTAL_HOSTS } from './freshdeskUrls'

export const DISALLOWED_LINK_PLACEHOLDER = '[link removed]'

// Scheme-required match: bare domains (e.g. "xero.com") are intentionally NOT
// linkified to avoid false positives in prose. Stops at whitespace / angle
// brackets. Case-insensitive so "HTTPS://" matches.
const URL_RE = /\bhttps?:\/\/[^\s<>]+/gi

// Freshdesk surfaces whose /tickets/<id> URLs collapse to a "#<id>" chip. The
// agent portal lives on *.freshdesk.com; the FP-facing portals are the
// Evolution Platform hosts (sourced from freshdeskUrls.ts - the single source
// of truth for those two hosts). Fixed product behaviour, so not Parameter-Store
// config (unlike the link-domain allow-list).
const TICKET_PATH_RE = /^\/(?:a|support)\/tickets\/(\d+)\b/

type Token = { type: 'text'; text: string } | { type: 'url'; raw: string }

type Classification =
  | { kind: 'freshdesk'; id: string; href: string }
  | { kind: 'allowed'; href: string }
  | { kind: 'disallowed' }
  | { kind: 'plain' } // unparseable URL - render verbatim as text

/**
 * Trim trailing sentence punctuation that the greedy URL match swallowed
 * (`see https://xero.com.` -> the `.` belongs to the sentence). A trailing `)`
 * is only trimmed when it's unbalanced within the match, so
 * `https://xero.com/a_(b)` keeps its closing paren.
 */
function trimTrailingPunctuation(url: string): string {
  let end = url.length
  while (end > 0) {
    const ch = url[end - 1]
    if ('.,;:!?'.includes(ch)) {
      end--
      continue
    }
    if (ch === ')') {
      const slice = url.slice(0, end)
      const opens = (slice.match(/\(/g) || []).length
      const closes = (slice.match(/\)/g) || []).length
      if (closes > opens) {
        end--
        continue
      }
    }
    break
  }
  return url.slice(0, end)
}

function tokenize(body: string): Token[] {
  const tokens: Token[] = []
  let lastIndex = 0
  for (const m of body.matchAll(URL_RE)) {
    const start = m.index ?? 0
    const matched = m[0]
    const raw = trimTrailingPunctuation(matched)
    const trailing = matched.slice(raw.length)
    if (start > lastIndex) {
      tokens.push({ type: 'text', text: body.slice(lastIndex, start) })
    }
    tokens.push({ type: 'url', raw })
    if (trailing) tokens.push({ type: 'text', text: trailing })
    lastIndex = start + matched.length
  }
  if (lastIndex < body.length) {
    tokens.push({ type: 'text', text: body.slice(lastIndex) })
  }
  return tokens
}

function toHttps(raw: string): string {
  return raw.replace(/^http:\/\//i, 'https://')
}

function hostAllowed(host: string): boolean {
  return getLinkDomains().some((d) => host === d || host.endsWith(`.${d}`))
}

function isFreshdeskHost(host: string): boolean {
  return (
    host === 'freshdesk.com' ||
    host.endsWith('.freshdesk.com') ||
    host in FRESHDESK_PORTAL_HOSTS
  )
}

/** Classify a raw URL string against the allow-list + Freshdesk rules. */
export function classifyUrl(raw: string): Classification {
  let url: URL
  try {
    url = new URL(raw)
  } catch {
    return { kind: 'plain' }
  }
  const host = url.hostname.toLowerCase().replace(/\.$/, '')
  const href = toHttps(raw)
  if (isFreshdeskHost(host)) {
    const m = url.pathname.match(TICKET_PATH_RE)
    if (m) return { kind: 'freshdesk', id: m[1], href }
  }
  if (hostAllowed(host)) return { kind: 'allowed', href }
  return { kind: 'disallowed' }
}

const linkSx = {
  color: poolwerxColors.teal,
  textDecoration: 'underline',
  wordBreak: 'break-all' as const,
  '&:hover': { color: poolwerxColors.tealHover },
}

const freshdeskSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  verticalAlign: 'baseline',
  color: poolwerxColors.teal,
  textDecoration: 'none',
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
  '&:hover': { textDecoration: 'underline' },
}

/** Render a note body into text + link nodes (drop straight into a Typography). */
export function linkifyNoteBody(body: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  let key = 0
  for (const tok of tokenize(body)) {
    if (tok.type === 'text') {
      nodes.push(tok.text)
      continue
    }
    const c = classifyUrl(tok.raw)
    if (c.kind === 'plain') {
      nodes.push(tok.raw)
      continue
    }
    if (c.kind === 'disallowed') {
      nodes.push(
        <Box
          key={key++}
          component="span"
          sx={{ color: 'text.disabled', fontStyle: 'italic' }}
        >
          {DISALLOWED_LINK_PLACEHOLDER}
        </Box>,
      )
      continue
    }
    if (c.kind === 'freshdesk') {
      nodes.push(
        <Link
          key={key++}
          href={c.href}
          target="_blank"
          rel="noopener noreferrer"
          sx={freshdeskSx}
        >
          <ConfirmationNumberOutlinedIcon sx={{ fontSize: '0.95em' }} />#{c.id}
        </Link>,
      )
      continue
    }
    nodes.push(
      <Link
        key={key++}
        href={c.href}
        target="_blank"
        rel="noopener noreferrer"
        sx={linkSx}
      >
        {tok.raw}
      </Link>,
    )
  }
  return nodes
}

/**
 * Return the raw URLs in a body whose host is NOT on the allow-list. Used by
 * the note-input validation to block submitting a note with a disallowed link.
 * Unparseable URL-like tokens are ignored (treated as plain text, not links).
 */
export function findDisallowedLinks(body: string): string[] {
  const bad: string[] = []
  for (const tok of tokenize(body)) {
    if (tok.type !== 'url') continue
    if (classifyUrl(tok.raw).kind === 'disallowed') bad.push(tok.raw)
  }
  return bad
}

/**
 * Build the user-facing "these links aren't allowed" message for a list of
 * disallowed raw URLs. Kept in lockstep with the backend `validate_note_links`
 * detail so the inline (block-on-submit) and server messages read the same.
 * Returns null when there are no disallowed links.
 */
export function disallowedLinksMessage(bad: string[]): string | null {
  if (!bad.length) return null
  const hosts: string[] = []
  for (const raw of bad) {
    let host = ''
    try {
      host = new URL(raw).hostname.toLowerCase()
    } catch {
      continue
    }
    if (host && !hosts.includes(host)) hosts.push(host)
  }
  const joined = hosts.join(', ')
  return (
    `Links to ${joined} aren't allowed in notes. Only links to approved ` +
    'systems (e.g. Xero, Lightspeed, Freshdesk, Poolwerx) can be saved.'
  )
}
